import { usePaywallStates } from '@/atoms/paywallAtoms';
import { IS_END_OF_90_DAY_TRIAL_EXPERIENCE_ENABLED } from '@/components/constants/flags';
import { openTeamTrialUpgradeRequestModalAtom } from '@/features/trialsAndUpgrades/team-trial-upgrade-request-modal/atoms';
import { trialRecapModalMessages } from '@/features/trialsAndUpgrades/trial-recap-modal/messages';
import { getPlanChangesHelpArticleUrl } from '@/features/trialsAndUpgrades/trial-recap-modal/utils';
import { openTrialUpgradeRequestCallbackModalAtom } from '@/features/trialsAndUpgrades/trial-upgrade-request-callback-modal/atoms';
import { useHasTrialUpgradeCallbackRequested } from '@/features/trialsAndUpgrades/trial-upgrade-request-callback-modal/useHasTrialUpgradeCallbackRequested';
import { getTeamContextAnalyticsProps } from '@/features/trialsAndUpgrades/utils/getTeamContextAnalyticsProps';
import {
  trackNinetyDayTrialRecapClick,
  trackNinetyDayTrialRecapViewed,
} from '@/models/analytics/features/trackNinetyDayTrialAnalytics';
import { trialRecapModalOpenAtom } from '@heidi/commerce';
import {
  NINETY_DAY_TRIAL_RECAP_ACTION,
  TRIALS_AND_UPGRADES_SCREEN,
  type NinetyDayTrialRecapActionToken,
} from '@heidi/commerce/contracts';
import { useReverseTrial, useUserTier } from '@heidi/commerce/data';
import { TierPracticeFilled, TierProFilled } from '@heidi/design-system/icons';
import { SubscriptionTier } from '@heidi/schema/ml-scribe';
import { useCombinedQueriesTeamGeneralInfo } from '@heidi/workspace/data';
import { useAtom, useSetAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useIntl, type MessageDescriptor } from 'react-intl';
import { useTrialRecapUsageStats } from './useTrialRecapUsageStats';

export function useTrialRecapModal() {
  const intl = useIntl();
  const { clinicianTrialDaysLeft: daysLeft } = useReverseTrial();
  const { userTier } = useUserTier();
  const [open, setOpen] = useAtom(trialRecapModalOpenAtom);
  const upgradeButtonRef = useRef<HTMLElement | null>(null);
  const { toggleClinicianUpgradeModal, togglePracticeUpgradeModal } = usePaywallStates();
  const { isTeamAdmin } = useTrialRecapUsageStats(open);
  const { hasTeam, isNonAdminTeamMember, isLoadingTeamDetails, isTeamStripeManaged } =
    useCombinedQueriesTeamGeneralInfo();
  const openTeamTrialUpgradeRequestModal = useSetAtom(
    openTeamTrialUpgradeRequestModalAtom
  );
  const hasCallbackRequested = useHasTrialUpgradeCallbackRequested();
  const openTrialUpgradeRequestCallbackModal = useSetAtom(
    openTrialUpgradeRequestCallbackModalAtom
  );

  const planChangesArticleUrl = useMemo(
    () => getPlanChangesHelpArticleUrl(intl.locale),
    [intl.locale]
  );

  const showNonAdminRequestBanner =
    IS_END_OF_90_DAY_TRIAL_EXPERIENCE_ENABLED() &&
    !isLoadingTeamDetails &&
    hasTeam &&
    isNonAdminTeamMember;

  const showContactSalesInsteadOfUpgrade =
    !isLoadingTeamDetails && isTeamAdmin === true && !isTeamStripeManaged;

  const isClinicianPlan = userTier === SubscriptionTier.CLINICIAN_PRO;
  const tierName = isClinicianPlan
    ? intl.formatMessage(trialRecapModalMessages.clinicianPlanName)
    : intl.formatMessage(trialRecapModalMessages.practicePlanName);
  const TierIcon = isClinicianPlan ? TierProFilled : TierPracticeFilled;

  // Title doesn't branch on usage — the Figma uses the same "What you've done"
  // / "Here's what your team's done" copy regardless of how much the user
  // engaged with Evidence, and the feature-list rows reflect zero usage by
  // hiding the right-hand badge rather than swapping out the heading.
  //
  // Descriptions branch on `isTeamAdmin` so the "you'll return to" vs "your
  // team will return to" wording matches the viewer.
  const titleMessage: MessageDescriptor = isClinicianPlan
    ? isTeamAdmin === true
      ? trialRecapModalMessages.titleTeamAdmin
      : trialRecapModalMessages.titleSoloClinician
    : isTeamAdmin === true
      ? trialRecapModalMessages.titleTeamAdmin
      : trialRecapModalMessages.titleSoloPractice;

  const descriptionMessage: MessageDescriptor = isClinicianPlan
    ? isTeamAdmin === true
      ? trialRecapModalMessages.descriptionClinicianTeamAdmin
      : trialRecapModalMessages.descriptionClinicianSolo
    : isTeamAdmin === true
      ? trialRecapModalMessages.descriptionPracticeTeamAdmin
      : trialRecapModalMessages.descriptionPracticeSolo;

  const upgradeCtaMessage: MessageDescriptor = isClinicianPlan
    ? trialRecapModalMessages.upgradeCtaClinician
    : trialRecapModalMessages.upgradeCtaPractice;

  useEffect(() => {
    if (!open) return;
    trackNinetyDayTrialRecapViewed({
      screen_name: TRIALS_AND_UPGRADES_SCREEN.ninetyDayTrialRecap,
      ...getTeamContextAnalyticsProps({
        hasTeam,
        isTeamAdmin,
        isTeamStripeManaged,
      }),
    });
    // Only fire when modal open state changes; team context is read at open-time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const trackRecapButton = useCallback(
    (action: NinetyDayTrialRecapActionToken, label: string) => {
      trackNinetyDayTrialRecapClick({
        screen_name: TRIALS_AND_UPGRADES_SCREEN.ninetyDayTrialRecap,
        action,
        label,
        ...(userTier !== undefined && { current_plan: userTier }),
      });
    },
    [userTier]
  );

  const handleUpgrade = useCallback(() => {
    trackRecapButton(
      NINETY_DAY_TRIAL_RECAP_ACTION.upgrade,
      intl.formatMessage(upgradeCtaMessage)
    );
    if (isClinicianPlan) {
      toggleClinicianUpgradeModal(true);
    } else {
      togglePracticeUpgradeModal(true);
    }
    setOpen(false);
  }, [
    intl,
    setOpen,
    trackRecapButton,
    upgradeCtaMessage,
    isClinicianPlan,
    toggleClinicianUpgradeModal,
    togglePracticeUpgradeModal,
  ]);

  const handleContactSalesClick = useCallback(() => {
    if (hasCallbackRequested) return;

    trackRecapButton(
      NINETY_DAY_TRIAL_RECAP_ACTION.contactSales,
      intl.formatMessage(
        isClinicianPlan
          ? trialRecapModalMessages.talkToUsKeepPlanCtaClinician
          : trialRecapModalMessages.talkToUsKeepPlanCtaPractice
      )
    );
    setOpen(false);
    openTrialUpgradeRequestCallbackModal();
  }, [
    hasCallbackRequested,
    intl,
    setOpen,
    isClinicianPlan,
    trackRecapButton,
    openTrialUpgradeRequestCallbackModal,
  ]);

  const handleRemindMeLater = useCallback(() => {
    trackRecapButton(
      NINETY_DAY_TRIAL_RECAP_ACTION.remindMeLater,
      intl.formatMessage(trialRecapModalMessages.remindMeLater)
    );
    setOpen(false);
  }, [intl, setOpen, trackRecapButton]);

  return {
    open,
    setOpen,
    upgradeButtonRef,
    daysLeft,
    isClinicianPlan,
    tierName,
    TierIcon,
    titleMessage,
    descriptionMessage,
    upgradeCtaMessage,
    planChangesArticleUrl,
    showNonAdminRequestBanner,
    showContactSalesInsteadOfUpgrade,
    handleUpgrade,
    handleContactSalesClick,
    handleRemindMeLater,
    openTeamTrialUpgradeRequestModal,
    hasCallbackRequested,
  };
}
